import React, { useContext, useEffect, useRef } from 'react';

import {
  INewStudentProvider,
  NewStudentProviderContext,
} from '../../../ProviderNewStudent';

export interface ISieadChatUser {
  id: string;
  name: string;
  email: string;
  phone_number: string;
  cpf?: string;
}

declare global {
  interface Window {
    __SIEAD_CHAT_USER__?: ISieadChatUser;
    __SIEAD_CHAT_TEARDOWN__?: () => void;
  }
}

const CHAT_WIDGET_BASE = (
  process.env.REACT_APP_CHAT_WIDGET_BASE ||
  'https://famart.edu.br/chat-whatsapp'
).replace(/\/$/, '');

interface IChatFloatingProps {
  student: boolean;
}

const ChatFloating: React.FC<IChatFloatingProps> = ({ student }) => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const { student: studentState } = useContext(
    NewStudentProviderContext,
  ) as INewStudentProvider;

  useEffect(() => {
    if (!student) {
      delete window.__SIEAD_CHAT_USER__;
      return;
    }

    const data = studentState?.dataStudent || readStoredStudent();
    if (!data?.cpf && !data?.email) return;

    window.__SIEAD_CHAT_USER__ = {
      id: String(data.email || data.cpf),
      name: data.name || '',
      email: data.email || '',
      phone_number: data.cellPhone || '',
      cpf: data.cpf ? String(data.cpf) : '',
    };
  }, [student, studentState?.dataStudent]);

  useEffect(() => {
    const scriptId = 'external-whatsapp-chat-script';
    const wantedFile = student ? '/student.js' : '/index.js';
    const existing = document.getElementById(
      scriptId,
    ) as HTMLScriptElement | null;
    const currentSrc = existing?.src || '';
    const isWanted = currentSrc.indexOf(wantedFile) !== -1;

    if (existing && !isWanted) {
      if (typeof window.__SIEAD_CHAT_TEARDOWN__ === 'function') {
        try {
          window.__SIEAD_CHAT_TEARDOWN__();
        } catch (err) {
          /* ignore */
        }
      }
      existing.remove();
      scriptRef.current = null;
    }

    if (!document.getElementById(scriptId)) {
      const script = createScriptElement(scriptId, student);
      document.body.appendChild(script);
      scriptRef.current = script;
    }

    return () => {
      if (student) {
        delete window.__SIEAD_CHAT_USER__;
      }
    };
  }, [student]);

  return null;
};

function readStoredStudent(): {
  name?: string;
  email?: string;
  cellPhone?: string;
  cpf?: string;
} | null {
  try {
    const parsed = JSON.parse(localStorage.getItem('dataStudent') || 'null');
    if (!parsed) return null;
    return parsed.dataStudent || parsed;
  } catch (err) {
    return null;
  }
}

function createScriptElement(
  scriptId: string,
  student: boolean,
): HTMLScriptElement {
  const script = document.createElement('script');
  script.id = scriptId;
  script.src = student
    ? `${CHAT_WIDGET_BASE}/student.js?v=${Date.now()}`
    : `${CHAT_WIDGET_BASE}/index.js?v=${Date.now()}`;
  script.type = 'text/javascript';
  script.async = true;
  script.onload = handleScriptLoad;
  script.onerror = handleScriptError;
  return script;
}

function handleScriptLoad(): void {
  if (typeof window !== 'undefined') {
    document.dispatchEvent(new Event('DOMContentLoaded'));
  }
}

function handleScriptError(): void {
  console.error('Erro ao carregar o script.');
}

export default ChatFloating;
